/**
 * AccessDenied — friendly 403 page shown when a user directly navigates
 * to a route they don't have permission to view.
 *
 * The sidebar already hides tabs a role can't reach (iter277), but that
 * only removes the entry point — anyone who types the URL, follows an
 * old bookmark, or clicks a stale deep-link would previously see the
 * raw page render with a broken data fetch or a bare "no data" screen.
 * This component renders instead so the boundary is explicit.
 */
import { useNavigate } from "react-router-dom";
import { ShieldOff, Home, ArrowLeft } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function AccessDenied({ requiredPermission }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div
      data-testid="access-denied-page"
      className="min-h-[calc(100vh-56px)] flex flex-col items-center justify-center px-6 py-12 text-center"
    >
      <div className="border border-[#27272A] bg-[#0F0F0F] p-10 max-w-xl w-full space-y-6">
        <div className="mx-auto size-14 flex items-center justify-center border border-[#FF3B30]/40 bg-[#FF3B30]/10">
          <ShieldOff className="size-7 text-[#FF3B30]" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#FF3B30] mb-2">Access denied</div>
          <h1 className="font-display text-2xl uppercase tracking-tight text-white">
            You don&apos;t have access to this page
          </h1>
          <p className="text-sm text-zinc-400 mt-3">
            Your current role doesn&apos;t include the permission required to view
            this workspace. If you think this is a mistake, contact an
            administrator to update your role.
          </p>
        </div>

        {requiredPermission && (
          <div className="text-xs text-zinc-500 border border-[#27272A] bg-[#0A0A0A] px-4 py-3 font-mono">
            Required permission: <span className="text-zinc-300">{requiredPermission}</span>
            {user?.role && (
              <>
                <br />
                Your role: <span className="text-zinc-300">{user.role}</span>
              </>
            )}
          </div>
        )}

        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            data-testid="access-denied-back"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-widest border border-[#27272A] text-zinc-300 hover:border-white hover:text-white transition-colors"
          >
            <ArrowLeft className="size-3.5" /> Back
          </button>
          <button
            data-testid="access-denied-home"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-widest border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white transition-colors"
          >
            <Home className="size-3.5" /> Return home
          </button>
        </div>
      </div>
    </div>
  );
}
